import { UpdateServiceRequestPayloadActions } from "../action/updateServiceRequestPayload-action";
import _cloneDeep from "lodash/cloneDeep";
import _isEmpty from "lodash/isEmpty";
import { removeDuplicateFields, sanitizeVehiclePayload } from "../utils";
import { ServiceRequest } from "../types";
import {processVehicleComment} from "../utilities/abg-util/vehicleCommentUtil";
import { processNoLocationCommentUpdate } from "../utilities/abg-util/LocationCommentUtil";

type ServiceRequestPayloadState = {
  assetDescription?: any;
  businessName?: string;
  incident?: any;
  partnerMemberId?: string;
  postFix?: any;
  promoCode?: string;
  memberId?: string;
  operatorId?: any;
  rentalDetail?: any;
  scheduledDateTime?: string;
  serviceRequest: ServiceRequest;
  sessionId: string;
  stackedMembership?: any;
  towDistance?: { scalar: number; unitMeasure: string };
  towReason?: string;
  usageId?: string;
  replacementVehicleNeeded?: boolean;
  commentRequest?: any;
  customerType?: string
};

const initialState: ServiceRequestPayloadState = {
  sessionId: "",
  serviceRequest: {
    additionalFields: [[]],
    appId: "",
    consumerId: "",
    contactNumber: "",
    deviceTimeZone: "CST",
    disablementAddress: {
      addressLine1: "",
      city: "",
      zipCode: "",
      country: "",
      state: "",
      latitude: 0,
      longitude: 0,
    },
    email: "",
    firstName: "",
    lastName: "",
    loggedIn: "N",
    partnerTransactionId: "",
    ppuFlag: "",
    smsOptIn: "Y",
  },
};

export default function (state: ServiceRequestPayloadState = initialState, action: UpdateServiceRequestPayloadActions): ServiceRequestPayloadState {
  let additionalFields = state.serviceRequest.additionalFields;
  switch (action.type) {
    case "UPDATE_APP_ID": {
      return {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          appId: action.payload,
        },
      };
    }
    case "UPDATE_SESSION_ID": {
      return {
        ...state,
        sessionId: action.payload,
      };
    }
    case "UPDATE_PROMO_CODE": {
      return {
        ...state,
        promoCode: action.payload,
      };
    }
    case "UPDATE_PARTNER_MEMBER_ID": {
      return {
        ...state,
        partnerMemberId: action.payload,
      };
    }
    case "UPDATE_MEMBER_ID": {
      return {
        ...state,
        memberId: action.payload,
      };
    }
    case "UPDATE_STACKED_INFO": {
      return {
        ...state,
        stackedMembership: action.payload,
      };
    }
    case "UPDATE_POST_FIX": {
      return {
        ...state,
        postFix: action.payload,
      };
    }
    case "UPDATE_USAGE_ID": {
      return {
        ...state,
        usageId: action.payload,
      };
    }
    case "UPDATE_TOW_REASON": {
      return {
        ...state,
        towReason: action.payload,
      };
    }
    case "UPDATE_REPLACEMENT_VEHICLE": {
      return {
        ...state,
        replacementVehicleNeeded: action.payload
      };
    }
    case "UPDATE_NO_DESTINATION_COMMENT_REQUEST": {
      const existingComment = state.commentRequest?.comment || "";
      const result = processNoLocationCommentUpdate(
        existingComment,
        action.payload
      );

      return {
        ...state,
        commentRequest: {
          commentType: "standard",
          comment: result
        }
      };
    }
    case "APPEND_VEHICLE_COMMENT_REQUEST": {
      const existingComment = state.commentRequest?.comment || "";
      const result = processVehicleComment(
        existingComment,
        action.payload
      );

      return {
        ...state,
        commentRequest: {
          commentType: "standard",
          comment: result
        }
      };
    }
    case "CLEAR_COMMENT_REQUEST": {
      delete state.commentRequest;
      return {
        ...state
      };
    }
    case "UPDATE_TOW_DISTANCE": {
      return {
        ...state,
        towDistance: action.payload,
      };
    }
    case "UPDATE_RENTAL_DETAIL": {
      return {
        ...state,
        rentalDetail: action.payload,
      };
    }
    case "UPDATE_SCHEDULED_SERVICE_DATE_TIME": {
      if (_isEmpty(action.payload)) {
        delete state.scheduledDateTime;
        return state;
      }
      return {
        ...state,
        scheduledDateTime: action.payload,
      };
    }
    case "CLEAR_SCHEDULED_SERVICE_DATE_TIME": {
      delete state.scheduledDateTime;
      return {
        ...state,
      };
    }
    case "CLEAR_DISABLEMENT_ADDRESS":
      let initialDisablementAddress = {
        ...initialState.serviceRequest.disablementAddress,
      };
      return {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          disablementAddress: initialDisablementAddress,
        },
      };
    case "UPDATE_DISABLEMENT_ADDRESS":
      let confirmedAddress = {
        addressLine1: action.payload.addressLine1,
        city: action.payload.city,
        zipCode: action.payload.zipCode,
        country: action.payload.country,
        state: action.payload.state,
        latitude: action.payload.latitude,
        longitude: action.payload.longitude,
      };
      return {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          disablementAddress: confirmedAddress,
        },
      };
    case "UPDATE_DESTINATION_ADDRESS":
      return {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          destinationAddress: action.payload,
        },
      };
    case "REMOVE_DESTINATION_ADDRESS":
      let newServiceRequest = { ...state.serviceRequest };
      delete newServiceRequest.destinationAddress;
      return {
        ...state,
        serviceRequest: newServiceRequest,
      };
    case "UPDATE_ADDITIONAL_COMMENTS":
      return {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          additionalComments: action.payload,
        },
      };
    case "UPDATE_FIRST_NAME":
      return {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          firstName: action.payload,
        },
      };
    case "UPDATE_LAST_NAME":
      return {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          lastName: action.payload,
        },
      };
    case "UPDATE_OPERATOR_ID":
      return {
        ...state,
        operatorId: action.payload,
      };
    case "UPDATE_SMS_OPT_IN":
      return {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          smsOptIn: action.payload,
        },
      };
    case "UPDATE_EMAIL":
      return {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          email: action.payload,
          consumerId: action.payload,
        },
      };
    case "UPDATE_ADDITIONAL_FIELDS":
      const [[key, value]] = Object.entries(action.payload);
      return {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          additionalFields: [removeDuplicateFields(key, value, additionalFields)],
        },
      };
    case "REMOVE_ADDITIONAL_FIELD":
      const newAdditionalFields = additionalFields[0].filter((field) => {
        return field.key !== action.payload;
      });
      return {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          additionalFields: [newAdditionalFields],
        },
      };

    case "UPDATE_CONTACT_NUMBER":
      const phoneNumber = action.payload.replace(/-/g, "");
      return {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          contactNumber: phoneNumber,
          additionalFields: [removeDuplicateFields("aniNbr", phoneNumber, additionalFields)],
        },
      };
    case "UPDATE_VEHICLE":
      let payloadCopy = _cloneDeep(action.payload);
      const sanitizedVehicle = sanitizeVehiclePayload(action.payload);
      let finalState = {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          vehicles: sanitizedVehicle,
        },
      };
      if (payloadCopy?.assetDescription) {
        finalState.assetDescription = payloadCopy?.assetDescription;
      }
      if (!!payloadCopy?.suspendIndicator) {
        finalState.serviceRequest.vehicles.suspendIndicator = payloadCopy?.suspendIndicator;
      }
      return finalState;
    case "UPDATE_CURRENT_MILEAGE":
      return {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          currentMileage: action.payload,
        },
      };
    case "UPDATE_PPU_FLAG":
      return {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          ppuFlag: action.payload,
        },
      };
    case "UPDATE_LOGGED_IN_FLAG":
      return {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          loggedIn: action.payload,
        },
      };
    case "UPDATE_CCARD_INFO":
      return {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          CCardInfo: {
            ...state.serviceRequest.CCardInfo,
            ...action.payload,
          },
        },
      };
    case "SET_PREVIOUS_TRANSACTION_AMOUNT":
      return {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          previousTransactionAmount: action.payload,
        },
      };
    case "CLEAR_CCARD_INFO":
      let newRequest = { ...state.serviceRequest };
      delete newRequest.CCardInfo;
      return {
        ...state,
        serviceRequest: newRequest,
      };
    case "CLEAR_SERVICE_TYPE_OPTIONS":
      delete state.serviceRequest?.serviceTypeOptions;
      return { ...state };
    case "UPDATE_SERVICE_TYPE_OPTIONS":
      return {
        ...state,
        serviceRequest: {
          ...state.serviceRequest,
          serviceTypeOptions: {
            ...action.payload,
          },
        },
      };
    case "UPDATE_BUSINESS_NAME":
      return {
        ...state,
        businessName: action.payload,
      };
    case "UPDATE_CUSTOMER_TYPE":
      return {
        ...state,
        customerType: action.payload,
      };
    case "UPDATE_INCIDENT_REPORT_DETAILS":
      return {
        ...state,
        incident: action.payload,
      };
    case "REFRESH_REDUCERS":
      return { ...initialState };
    case "CLEAR_INCIDENT_DATA":
      delete state.incident;
      return {
        ...state,
      };
    default:
      return state;
  }
}
